import * as React from 'react';
import { useParams } from 'react-router';

import styled from '@emotion/styled';

import { Spacer } from '@shared/components/helpers';
import { useURLSearchParam } from '@shared/hooks/use_url_search_param';

import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { accountURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { LOGISTICS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  Logistics__PurchaseOrderInput,
  useLogisticsPurchaseOrderQuery,
  useAccountBreadCrumbsQueryQuery,
} from '@admin/schema';

// import { LogisticsPurchaseOrdersQuery, useLogisticsPurchaseOrdersQuery } from '@admin/schema';
import { AccountBreadcrumbs } from '../breadcrumbs/account_breadcrumbs';
import { LogisticsPurchaseOrderForm, DEFAULT_ADDRESS } from './form/form';

const LargeFont = styled.div`
  font-size: 2.5em;
`;

export const LogisticsEditPurchaseOrder: React.FC = () => {
  useTitle('Edit Purchase Order');
  const { id } = useParams<{ id: string }>();
  const [accountID] = useURLSearchParam('accountID');

  const account = useAccountBreadCrumbsQueryQuery({ client, variables: { accountID: accountID! } }).data?.account;
  const purchaseOrder = useLogisticsPurchaseOrderQuery({ client, variables: { id } }).data?.purchaseOrder;

  const onSubmit = async () => {
    window.location.href = accountURL({ id: accountID! });
  };

  if (!purchaseOrder) {
    return <Spinner />;
  }

  const formData: Logistics__PurchaseOrderInput = {
    accountID: accountID!,
    id: purchaseOrder.id,
    number: purchaseOrder.number,
    fulfillmentMethod: purchaseOrder.fulfillmentMethod,
    shipToAddress:
      (purchaseOrder.shipTo && {
        street: purchaseOrder.shipTo.street,
        aptsuite: purchaseOrder.shipTo.aptsuite,
        city: purchaseOrder.shipTo.city,
        state: purchaseOrder.shipTo.state,
        zip: purchaseOrder.shipTo.zip,
        country: purchaseOrder.shipTo.country,
        latitude: purchaseOrder.shipTo.latitude,
        longitude: purchaseOrder.shipTo.longitude,
        businessName: purchaseOrder.shipTo.businessName,
      }) ??
      DEFAULT_ADDRESS,
    fulfillmentExpectations: purchaseOrder.fulfillmentExpectations.map((fe) => ({
      fulfillmentExpectationID: fe.id,
      skuID: fe.sku.id,
      quantity: fe.quantity,
    })),
    phoneNumber: purchaseOrder.phoneNumber,
  };

  return (
    <Permission resource={LOGISTICS.resource} action={LOGISTICS.actions.update}>
      {account && (
        <LargeFont>
          <AccountBreadcrumbs account={account} newItem={`Edit Purchase Order ${purchaseOrder.number}`} />
        </LargeFont>
      )}
      <Spacer height="10px" />
      <LogisticsPurchaseOrderForm formData={formData} onSave={onSubmit} />
    </Permission>
  );
};
